import { FlexColumn, FlexRow, LabeledField, StyledButton, useLoading } from '@gorila-shared-ui/components';
import Papa from 'papaparse';
import { FileRow } from 'baseui/file-uploader';
import { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';
import { FEEDBACK, FEEDBACK_PREFIXES, REQUIRED_FIELD_TEXT } from '../../constants/app';
import { useFeedback } from '../../hooks/useFeedback';
import { useForms } from '../../hooks/useForms';
import { useStyles } from '../../hooks/useStyles';
import { postMassiveCommand } from '../../services/massiveCommandService';
import {
  editMassiveErrorState,
  editMassiveState,
  editMassiveTouchedState,
  editMassiveValidState,
} from '../../storage/massiveComms';
import { MassiveCommandRequest, MassiveCommandRequestTouched } from '../../types/massiveCommands';
import { CSVPreview } from '../shared/CSVPreview';
import { DocumentUploader } from '../shared/DocumentUploader';
import { AvailableCommandsSelect } from '../shared/selects/AvailableCommandsSelect';
import { DeviceModelSelect } from '../shared/selects/DeviceModelSelect';

type Props = Readonly<{
  onCancel: () => void;
  afterSave: () => void;
}>;

export function MassiveCommsForm({ onCancel, afterSave }: Props) {
  const { css, theme } = useStyles();
  const [form, setForm] = useRecoilState(editMassiveState);
  const [formTouched, setFormTouched] = useRecoilState(editMassiveTouchedState);
  const formValid = useRecoilValue(editMassiveValidState);
  const formHasError = useRecoilValue(editMassiveErrorState);
  const resetEditMassiveState = useResetRecoilState(editMassiveState);
  const resetEditMassiveTouchedState = useResetRecoilState(editMassiveTouchedState);
  const { onTouched, onChangeInput } = useForms<MassiveCommandRequest, MassiveCommandRequestTouched>(
    setForm,
    setFormTouched,
  );
  const { startLoading, stopLoading, loading } = useLoading(false);
  const { showFailFeedback, showPositiveFeedback } = useFeedback();
  const [uploader, setUploader] = useState<{ data: FileRow }>();
  const fileIsValid = uploader?.data?.status === 'processed';
  const canSubmit = !formHasError;
  const fileError = form.idents.length <= 0 ? 'El archivo está vacío' : undefined;

  useEffect(() => {
    return () => {
      clearStates();
    };
  }, []);

  useEffect(() => {
    if (uploader?.data?.status === 'processed') {
      Papa.parse(uploader.data.file, {
        complete: (result) => {
          const rows = result.data.slice(1) as string[][];
          if (rows.length) {
            onChangeInput(
              'idents',
              rows.map((row) => row[0]),
            );
          } else {
            setUploader((prev) => (prev ? { data: { ...prev.data, status: 'error' } } : undefined));
          }
        },
        header: false,
      });
    } else {
      onChangeInput('idents', []);
    }
  }, [uploader]);

  const onSave = async () => {
    if (!canSubmit) return;
    startLoading();
    const { error } = await postMassiveCommand(form);
    if (!error) {
      afterSave();
      showPositiveFeedback(FEEDBACK.created(FEEDBACK_PREFIXES.massiveCommands));
    } else {
      showFailFeedback(error || FEEDBACK.failedCreation(FEEDBACK_PREFIXES.massiveCommands));
    }
    stopLoading();
  };

  const clearStates = () => {
    resetEditMassiveState();
    resetEditMassiveTouchedState();
  };

  return (
    <FlexColumn
      classNames={css({
        justifyContent: 'space-between',
        overflow: 'hidden',
      })}
    >
      <FlexColumn
        classNames={css({
          overflow: 'auto',
          height: '100%',
        })}
      >
        <DeviceModelSelect
          label="Modelo de los dispositivos:"
          onChangeDeviceModel={(deviceModelId) => onChangeInput('deviceModelId', deviceModelId)}
          isFilter={false}
          selectedDeviceModelId={form.deviceModelId}
          onBlur={() => onTouched('deviceModelTouched')}
          error={formTouched.deviceModelTouched && !formValid.deviceModelId && REQUIRED_FIELD_TEXT}
          required
        />
        <AvailableCommandsSelect
          label="Commandos disponibles:"
          onChangeCommand={(command) => onChangeInput('command', command)}
          deviceModelId={form.deviceModelId}
          selectedCommandId={form.command}
          disabled={!form.deviceModelId}
          onBlur={() => onTouched('commandTouched')}
          error={formTouched.commandTouched && !formValid.command && REQUIRED_FIELD_TEXT}
          required
        />
        <LabeledField
          label="Archivo:"
          error={formTouched.fileTouched && !uploader && REQUIRED_FIELD_TEXT}
          required
        >
          <DocumentUploader
            acceptExtensions={['.csv']}
            onFileChange={(fileRow) => {
              setUploader(
                fileRow
                  ? {
                      data: fileRow,
                    }
                  : undefined,
              );
            }}
            externalValidationError={fileError}
            file={uploader?.data}
            onBlur={() => onTouched('fileTouched')}
          />
        </LabeledField>
        {fileIsValid && (
          <CSVPreview
            file={uploader.data.file}
            onChange={(file) => {
              setUploader({ data: { ...uploader.data, file } });
            }}
          />
        )}
      </FlexColumn>
      <FlexRow classNames={`${css({ alignItems: 'center', justifyContent: 'flex-end' })}`}>
        <FlexRow gap={theme.sizing.scale300}>
          <StyledButton
            kind="tertiary"
            onClick={onCancel}
          >
            Cancelar
          </StyledButton>
          <StyledButton
            onClick={onSave}
            isLoading={loading}
            disabled={!canSubmit}
          >
            Guardar
          </StyledButton>
        </FlexRow>
      </FlexRow>
    </FlexColumn>
  );
}
